import { jwtDecode } from 'jwt-decode';
import type { User } from 'oidc-client-ts';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

import type { organization } from '../types/oidcUser';

import { getAuthority } from '@utils/auth';
import { useRefreshToken } from './apiHooks/tokenValidationHooks';

const { VITE_COMPANION_CLIENT_ID } = import.meta.env;

interface CustomJwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  organization?: organization[];
  role?: string;
  exp?: number;
  scope?: string[];
}

export const useTokenRefresh = () => {
  const auth = useAuth();
  const {
    mutateAsync: refreshTokenAsync,
    isPending,
    isError,
    error,
  } = useRefreshToken();

  const refreshAccessToken = useCallback(
    async (refreshTokenValue: string) => {
      try {
        const { access_token, id_token, refresh_token } =
          await refreshTokenAsync(refreshTokenValue);

        // Update localStorage with new token
        const LSKey = `oidc.user:${getAuthority()}:${
          VITE_COMPANION_CLIENT_ID as string
        }`;

        if (access_token) {
          const userData = jwtDecode<CustomJwtPayload>(access_token);

          const oidcUser = {
            access_token: access_token,
            id_token: id_token || undefined,
            refresh_token,
            token_type: 'Bearer',
            profile: {
              sub: userData.sub || '',
              email: userData.email || '',
              name: userData.name || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              organization: userData.organization || [],
              role: userData.role,
              // Add required IdTokenClaims properties
              iss: '',
              aud: '',
              exp: userData.exp || Math.floor(Date.now() / 1000) + 3600,
              iat: Math.floor(Date.now() / 1000),
            },
            expires_at: userData.exp,
            scope:
              userData?.scope?.join(' ') ||
              'openid profile offline_access napsterai.companions.identity.main',
            session_state: null,
            state: null,
            expires_in: userData.exp
              ? userData.exp - Math.floor(Date.now() / 1000)
              : 3600,
            expired: false,
            scopes:
              userData.scope ||
              'openid profile offline_access napsterai.companions.identity.main',
            toStorageString: function () {
              return JSON.stringify(this);
            },
          };

          localStorage.setItem(LSKey, JSON.stringify(oidcUser));

          // Sync the updated user with auth context
          await auth.events.load(oidcUser as unknown as User);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        throw error;
      }
    },
    [refreshTokenAsync, auth.events],
  );

  return {
    refreshAccessToken,
    isPending,
    isError,
    error,
  };
};
